import { Link } from 'wouter';
import { I, KebabIcon } from '../lib/icons';
import { ActionMenu } from './ui/ActionMenu';
import { Pill } from './ui/Pill';
import type { CollectionWithMeta } from '../../shared/collection-types';

/**
 * Collection-Card mit 2×2-Mosaic der frühesten 4 cover-thumbnails (Sprint B
 * PR-B1 DK-12).
 *
 * Layout: 2×2 grid (CSS-Grid), aspect-2/1. Bei weniger als 4 thumbs werden
 * leere Tiles mit einem Asset-Icon-Placeholder gefüllt. Card ist eine
 * `<Link>` zu /collections/:id — Hover-State subtle bg + shadow.
 *
 * Empty-State: alle 4 tiles sind icon-placeholder wenn `coverThumbnails`
 * leer ist (Collection ohne Assets oder alle Assets soft-deleted).
 *
 * Opt-in `onDuplicate`: rendert ein ⋯-Overflow-Menü (oben rechts) mit
 * „Duplizieren". Das Menü liegt als Sibling NEBEN dem `<Link>` (nicht darin) —
 * ein `<button>` darf nicht in ein `<a>` genistet werden (nested-interactive,
 * invalid HTML). Darum Root = nicht-interaktiver `relative`-Wrapper, der die
 * Hover-/Border-Optik vom Link erbt, und der Link umschließt nur Mosaik+Text.
 */
export function CollectionCardMosaic({
  collection,
  onDuplicate,
  duplicating = false,
}: {
  collection: CollectionWithMeta;
  onDuplicate?: () => void;
  duplicating?: boolean;
}) {
  // Pad auf 4 tiles — null = empty placeholder
  const tiles: (string | null)[] = [
    ...collection.coverThumbnails,
    null,
    null,
    null,
    null,
  ].slice(0, 4);

  return (
    <div className="relative">
      {onDuplicate && (
        <div className="absolute top-2 right-2 z-10">
          <ActionMenu
            label={<KebabIcon />}
            ariaLabel="Weitere Aktionen"
            variant="secondary"
            size="sm"
            className="min-w-9 px-2.5 shadow-sm"
            items={[
              {
                label: duplicating ? 'Wird dupliziert…' : 'Duplizieren',
                icon: <I.copy size={14} />,
                onSelect: onDuplicate,
              },
            ]}
          />
        </div>
      )}
      <Link
        href={`/collections/${collection.id}`}
        className="block bg-white rounded-xl border border-ink-200 overflow-hidden hover:border-brand-green-700/40 hover:shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2"
      >
      <div
        className="grid grid-cols-2 gap-px bg-ink-200 aspect-[2/1]"
        aria-hidden="true"
      >
        {tiles.map((src, i) => (
          <div
            key={i}
            className="bg-brand-cream flex items-center justify-center overflow-hidden"
          >
            {src ? (
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <I.image className="w-6 h-6 text-brand-green-300" />
            )}
          </div>
        ))}
      </div>
      <div className="p-5">
        <h3 className="text-brand-green-700 font-bold tracking-tight text-lg mb-1.5 truncate">
          {collection.name}
        </h3>
        {collection.description && (
          <p className="text-sm text-brand-green-600 line-clamp-2 mb-3">
            {collection.description}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs2 font-mono text-brand-green-600">
          <Pill tone="neutral">{collection.assetCount} Assets</Pill>
          <span className="text-brand-green-300">·</span>
          <span>{formatRelative(collection.updatedAt)}</span>
        </div>
      </div>
      </Link>
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'eben';
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} T`;
  return new Date(iso).toLocaleDateString('de-DE');
}
